import React, { useRef, useEffect, useState } from 'react';

interface CameraProps {
  onVideoReady: (video: HTMLVideoElement) => void;
  showDetectionZone?: boolean;
  detectionZone?: { x: number; y: number; width: number; height: number };
}

export const Camera = ({
  onVideoReady,
  showDetectionZone = false,
  detectionZone,
}: CameraProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  streamRef.current = stream;

  useEffect(() => {
    startCamera();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
    // Only re-run when user switches camera; startCamera reads current stream from state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facingMode]);

  useEffect(() => {
    if (showDetectionZone && detectionZone && videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.strokeStyle = '#00ff00';
        ctx.lineWidth = 3;
        ctx.strokeRect(detectionZone.x, detectionZone.y, detectionZone.width, detectionZone.height);
      }
    }
  }, [showDetectionZone, detectionZone]);

  const startCamera = async () => {
    try {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: false,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play();
          if (videoRef.current) {
            onVideoReady(videoRef.current);
          }
        };
      }

      setStream(mediaStream);
    } catch (err) {
      console.error('Camera access error:', err);
    }
  };

  const toggleCamera = () => {
    setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
  };

  return (
    <div className="relative">
      <video
        ref={videoRef}
        className="w-full max-w-[640px] rounded-lg block"
        playsInline
        muted
        aria-label="Camera feed for juggling detection"
      />
      {showDetectionZone && (
        <canvas
          ref={canvasRef}
          className="absolute top-0 left-0 w-full max-w-[640px] pointer-events-none"
          aria-hidden
        />
      )}
      <button
        type="button"
        onClick={toggleCamera}
        className="absolute top-2 right-2 py-2 px-4 text-base cursor-pointer bg-white/90 hover:bg-white border border-gray-300 rounded shadow focus:outline-none focus:ring-2 focus:ring-green-500"
      >
        Switch Camera
      </button>
    </div>
  );
};
